import React, { Component } from 'react';
import { bool, node, shape, string } from 'prop-types';
import { withStyles } from 'material-ui/styles';
import classNames from 'classnames';
import ButtonBase from 'material-ui/ButtonBase';
import T from '../Text';
import { mainColor, secondaryColor, white, darkGray } from '../styles/colors';
import {
  label as labelStyles,
  labelS,
  fullWidth,
  sizeL,
  sizeM,
  sizeS,
  sizeXS,
} from './styles';
import { reduceClasses } from '../utils/testUtils';

export const styles = ({ breakpoints }) => ({
  defaultLabel: {
    '& > span': labelStyles,
  },
  smallLabel: {
    '& > span': labelS,
  },
  white: {
    backgroundColor: 'transparent',
    boxShadow: `inset 0px 0px 0px 1px ${white}`,
    '& > span': {
      color: white,
    },
    [breakpoints.up('sm')]: {
      '&:hover': {
        backgroundColor: white,
        boxShadow: 'none',
        '& > span': {
          color: mainColor,
        },
      },
    },
  },
  primary: {
    backgroundColor: 'transparent',
    boxShadow: `inset 0px 0px 0px 1px ${mainColor}`,
    '& > span': {
      color: mainColor,
    },
    [breakpoints.up('sm')]: {
      '&:hover': {
        backgroundColor: mainColor,
        boxShadow: 'none',
        '& > span': {
          color: white,
        },
      },
    },
  },
  accent: {
    backgroundColor: 'transparent',
    boxShadow: `inset 0px 0px 0px 1px ${secondaryColor}`,
    '& > span': {
      color: secondaryColor,
    },
    [breakpoints.up('sm')]: {
      '&:hover': {
        backgroundColor: secondaryColor,
        boxShadow: 'none',
        '& > span': {
          color: white,
        },
      },
    },
  },
  disabled: {
    opacity: 0.28,
  },
  disabledPrimary: {
    backgroundColor: 'transparent',
    boxShadow: `inset 0px 0px 0px 1px ${darkGray}`,
    opacity: 0.36,
    '& > span': {
      color: darkGray,
    },
  },
  sizeL: sizeL(breakpoints),
  sizeM,
  sizeS,
  sizeXS,
  fullWidth,
});

class InverseButton extends Component {
  state = {
    text: white,
    textHover: mainColor,
    hover: false,
  };

  componentWillMount() {
    this.verifyStyles(this.props);
  }

  componentWillReceiveProps(newProps) {
    const { primary, accent, textHoverColor, disabled } = this.props;

    if (
      newProps.primary !== primary ||
      newProps.accent !== accent ||
      newProps.textHoverColor !== textHoverColor
    ) {
      this.verifyStyles(newProps);
    }
    if (newProps.disabled && newProps.disabled !== disabled) {
      this.toggleLabel(false);
    }
  }

  setStyles = newState => this.setState({ ...newState });

  verifyStyles = props => {
    const { primary, accent, textHoverColor } = props;
    let text = white;
    let textHover = textHoverColor || mainColor;

    if (primary) {
      text = mainColor;
      textHover = textHoverColor || white;
    } else if (accent) {
      text = secondaryColor;
      textHover = textHoverColor || white;
    }
    this.setStyles({
      text,
      textHover,
    });
  };

  toggleLabel = hover => {
    if (this.state.hover !== hover) {
      this.setState({ hover });
    }
  };

  render() {
    const {
      accent,
      children,
      disabled,
      full,
      classes: classesProp,
      className: classNameProp,
      label,
      primary,
      l,
      s,
      xs,
      small,
      textHoverColor, // eslint-disable-line no-unused-vars
      test,
      ...other
    } = this.props;
    const { hover } = this.state;
    const classes = test ? reduceClasses(classesProp) : classesProp;

    return (
      <ButtonBase
        className={classNames(
          {
            [classes.disabled]: disabled,
            [classes.disabledPrimary]: disabled && (primary || accent),
            [classes.white]: !primary && !accent,
            [classes.accent]: !disabled && accent,
            [classes.primary]: !disabled && primary,
            [classes.sizeL]: l,
            [classes.sizeM]: !l && !s && !xs && !small,
            [classes.sizeS]: s,
            [classes.sizeXS]: xs || small,
            [classes.fullWidth]: full,
          },
          s || xs ? classes.smallLabel : classes.defaultLabel,
          classNameProp,
        )}
        disabled={disabled}
        focusRipple
        onMouseEnter={() => {
          this.toggleLabel(true);
        }}
        onMouseLeave={() => {
          this.toggleLabel(false);
        }}
        onMouseOver={() => {
          this.toggleLabel(true);
        }}
        onMouseOut={() => {
          this.toggleLabel(false);
        }}
        {...other}
      >
        {children || (
          <T
            color={hover && textHoverColor ? textHoverColor : undefined}
            test={test}
          >
            {label}
          </T>
        )}
      </ButtonBase>
    );
  }
}

InverseButton.propTypes = {
  /** Sets accent background color. */
  accent: bool,
  /** Optional children. Only rendered if no label was provided. */
  children: node,
  /** Classes from the styleSheet generated. */
  classes: shape({}).isRequired,
  /** Useful to extend the styleSheet applied to the button. */
  className: string,
  /** If true, the button will be disabled. */
  disabled: bool,
  /** Large button styles (Button L). */
  l: bool,
  /** Small button styles (Button S). */
  s: bool,
  /** Smaller button styles (Button S padding 16px). */
  xs: bool,
  /** Set button width to 100% to fit its container. */
  full: bool,
  /** Label for the button. */
  label: string,
  /** Sets primary background color. */
  primary: bool,
  /** Smaller padding values and font-size. */
  small: bool,
  /** Label color to be applied on button hover. Default is #0099FF, and #FFFFFF if either primary or accent is true.. */
  textHoverColor: string,
  /** Useful for snapshot testing. */
  test: bool,
};

InverseButton.defaultProps = {
  accent: false,
  disabled: false,
  children: undefined,
  className: undefined,
  l: false,
  s: false,
  xs: false,
  full: false,
  label: undefined,
  primary: false,
  small: false,
  textHoverColor: undefined,
  test: false,
};

export default withStyles(styles)(InverseButton);
